import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { formatCurrency, COURSE_LEVEL_LABELS } from "@learn-me/utils";
import {
  Plus,
  Edit,
  Trash2,
  BookOpen,
  Eye,
  EyeOff,
  Sparkles,
  Archive,
  ArchiveRestore,
  Copy,
  Wand2,
  ChevronDown,
  FileText,
} from "lucide-react";
import toast from "react-hot-toast";
import { AIGenerationModal } from "@/features/admin/components/AIGenerationModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { handleApiError } from "@/lib/errorHandler";
import { normalizeImageUrl } from "@/lib/imageUrl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@learn-me/ui";
import { useNavigate } from "react-router-dom";

export default function CoursesManagementPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    course: { id: string; title: string } | null;
  }>({
    isOpen: false,
    course: null,
  });
  const [archiveDialog, setArchiveDialog] = useState<{
    isOpen: boolean;
    course: { id: string; title: string; isArchived: boolean } | null;
  }>({
    isOpen: false,
    course: null,
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-courses", page],
    queryFn: () => adminApi.getCourses(page),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Curso eliminado");
      setDeleteDialog({ isOpen: false, course: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al eliminar el curso");
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.archiveCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Curso archivado exitosamente");
      setArchiveDialog({ isOpen: false, course: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al archivar el curso");
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.unarchiveCourse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Curso restaurado exitosamente");
      setArchiveDialog({ isOpen: false, course: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al restaurar el curso");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adminApi.duplicateCourse(id),
    onSuccess: (newCourse) => {
      queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success(`Curso duplicado: ${newCourse.title}`);
    },
    onError: (error) => {
      handleApiError(error, "Error al duplicar el curso");
    },
  });

  const handleDeleteClick = (course: { id: string; title: string }) => {
    setDeleteDialog({ isOpen: true, course });
  };

  const handleArchiveClick = (course: {
    id: string;
    title: string;
    status: string;
  }) => {
    setArchiveDialog({
      isOpen: true,
      course: { ...course, isArchived: course.status === "ARCHIVED" },
    });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.course) {
      deleteMutation.mutate(deleteDialog.course.id);
    }
  };

  const handleConfirmArchive = () => {
    if (archiveDialog.course) {
      if (archiveDialog.course.isArchived) {
        unarchiveMutation.mutate(archiveDialog.course.id);
      } else {
        archiveMutation.mutate(archiveDialog.course.id);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Cursos</h1>
          <p className="text-muted-foreground">
            Gestiona los cursos de la plataforma.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/courses/new">
            <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
              <Wand2 className="mr-2 h-4 w-4" />
              Crear Curso con IA
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => navigate("/admin/courses/new-manual")}
              >
                <FileText className="mr-2 h-4 w-4" />
                Crear manualmente
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsAIModalOpen(true)}>
                <Sparkles className="mr-2 h-4 w-4" />
                Generación rápida con IA
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* AI Generation Modal */}
      <AIGenerationModal
        isOpen={isAIModalOpen}
        onClose={() => {
          setIsAIModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["admin-courses"] });
        }}
      />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 flex gap-4">
                <div className="w-40 h-24 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-1/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.items || data.items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No hay cursos</h2>
            <p className="text-muted-foreground mb-6">
              Crea tu primer curso con ayuda de la inteligencia artificial.
            </p>
            <Link to="/admin/courses/new">
              <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                <Wand2 className="mr-2 h-4 w-4" />
                Crear Curso con IA
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data.items.map((course) => (
              <Card key={course.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-40 h-24 bg-muted rounded overflow-hidden flex-shrink-0">
                    {course.thumbnailUrl ? (
                      <img
                        src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{course.title}</h3>
                      {course.status === "PUBLISHED" ? (
                        <Badge variant="success" className="flex-shrink-0">
                          <Eye className="h-3 w-3 mr-1" />
                          Publicado
                        </Badge>
                      ) : course.status === "ARCHIVED" ? (
                        <Badge variant="secondary" className="flex-shrink-0">
                          <Archive className="h-3 w-3 mr-1" />
                          Archivado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex-shrink-0">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Borrador
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                      {course.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-medium">
                        {course.isFree
                          ? "Gratis"
                          : formatCurrency(
                              course.salePrice ?? 0,
                              course.currency,
                            )}
                      </span>
                      {course.level && (
                        <Badge variant="outline">
                          {COURSE_LEVEL_LABELS[course.level] ?? course.level}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link to={`/admin/courses/${course.id}`}>
                      <Button variant="outline" size="sm" title="Editar">
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => duplicateMutation.mutate(course.id)}
                      disabled={duplicateMutation.isPending}
                      title="Duplicar curso"
                    >
                      <Copy className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleArchiveClick({
                          id: course.id,
                          title: course.title,
                          status: course.status,
                        })
                      }
                      disabled={
                        archiveMutation.isPending || unarchiveMutation.isPending
                      }
                      title={
                        course.status === "ARCHIVED" ? "Restaurar" : "Archivar"
                      }
                    >
                      {course.status === "ARCHIVED" ? (
                        <ArchiveRestore className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Archive className="h-4 w-4 text-amber-600" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleDeleteClick({
                          id: course.id,
                          title: course.title,
                        })
                      }
                      disabled={deleteMutation.isPending}
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4">
                Pagina {page} de {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === data.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, course: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar Curso"
        description={`¿Estás seguro de eliminar "${deleteDialog.course?.title}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Archive/Unarchive Confirmation Dialog */}
      <ConfirmDialog
        isOpen={archiveDialog.isOpen}
        onClose={() => setArchiveDialog({ isOpen: false, course: null })}
        onConfirm={handleConfirmArchive}
        title={
          archiveDialog.course?.isArchived
            ? "Restaurar Curso"
            : "Archivar Curso"
        }
        description={
          archiveDialog.course?.isArchived
            ? `¿Deseas restaurar el curso "${archiveDialog.course?.title}"? El curso volverá a estado borrador.`
            : `¿Deseas archivar el curso "${archiveDialog.course?.title}"? Los estudiantes inscritos mantendrán su acceso, pero el curso no será visible para nuevas inscripciones.`
        }
        confirmText={
          archiveDialog.course?.isArchived ? "Restaurar" : "Archivar"
        }
        variant={archiveDialog.course?.isArchived ? "info" : "warning"}
        isLoading={archiveMutation.isPending || unarchiveMutation.isPending}
      />
    </div>
  );
}
